/**
 * @module app.cd
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { modelCodeToResourceKey } from 'app/shared/lib/i18n';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { ComponentBase, StandardLoadOptions, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { localSort, INestedLocalSorting } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { deepClone } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

type ShowLinks = {
    app: number,
    canPreviewDownloads: boolean,
    showSummaryLink: boolean,
    openSummary?: (id: string, number: string) => void,
    transitionalSummaryId?: number,
    previewTransitionalPageId: number
}

@autoinject()
export class MemberCorrespondence extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId!: string;

    private correspondenceConfig: ICorrespondenceConfig = {
        transitionalPageId: '604',
        moduleCode: 'CD',
        serviceRootUrl: 'cd/members',
        rollupRootUrl: 'cd/members/rollup/correspondence',
        targetType: 'CD.Member',
        targetColumnHeader: 'Member #',
        targetColumnProperty: 'member.identifier.primary',
        targetInclude: ['member', 'attachedTo.type'],
        rollupByMember: true,
        targetSummaryTooltip: 'cd:member.summary.$long'
    }

    protected memberLetters: ReadonlyArray<CD.MemberRollupCorrespondenceLetter> = [];
    protected memberLettersClone: ReadonlyArray<CD.MemberRollupCorrespondenceLetter> = [];

    private sorting: Sorting = new Sorting();
    private nestedLocalSorting: INestedLocalSorting = {};
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    private showLinks: ReadonlyArray<ShowLinks> = [];
    protected canViewHistory: boolean = false;
    protected hasAuthRoute = false;
    protected hasCaseRoute = false;

    private openCaseSummary = openCaseSummary;
    private openAuthSummary = openAuthSummary;
    protected modelCodeToResourceKey = modelCodeToResourceKey;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CorrespondenceService,
        private readonly formatProvider: FormatProvider,
        private readonly transitional: TransitionalManager,
        protected readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'metadata.modified.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }
    protected bind(): void {
        //never async.
        this.service.configure(this.correspondenceConfig);
        this.reload();
    }

    ////****************** Events ***************************************************////

    public async reload(): Promise<void> {
        await this.loadMemberLetters();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'metadata.modified.timestamp',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected applySortCopies(event: SortChangeEvent, copies: Core.CorrespondenceLetterCopy[], index: number, parent: CD.MemberRollupCorrespondenceLetter): void {
        const { property, direction } = event.detail;
        const { nestedLocalSorting } = this;
        nestedLocalSorting[index] = nestedLocalSorting[index] || new Sorting();

        if(direction === null) {
            nestedLocalSorting[index].remove(property);
        } else {
            nestedLocalSorting[index].add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.nestedLocalSorting[index].entries.length > 0) {
            localSort(copies, nestedLocalSorting[index]);
        } else {
            this.memberLetters.map((memberLetter, index) => {
                if(memberLetter.id === parent.id) {
                    // If any sorts are made to parent table, clone needs to be updated.
                    memberLetter.copies = deepClone(this.memberLettersClone[index].copies || []);
                }
            });
        }
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canViewHistory = this.accountContext.hasRouteAccess('613');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');

        this.showLinks = [
            {
                app: 2001,
                canPreviewDownloads: true,// this.accountContext.hasPermission('CS.CaseCorrespondenceLetter.Download.Preview'),
                showSummaryLink: this.accountContext.hasRouteAccess('2007'),//'cs.issue.summary'
                transitionalSummaryId: 2007,
                previewTransitionalPageId: 2027 //2028 would be Provider.
            },
            {
                app: 3001,
                canPreviewDownloads: this.accountContext.hasPermission('CM.CaseCorrespondenceLetter.Download.Preview'),
                showSummaryLink: this.accountContext.hasRouteAccess('cm.case.summary'),
                openSummary: (id: string, number: string) => {
                    const c = { id, number, member: { id: this.memberId } } as CM.Case;
                    this.openCaseSummary(c, this.hasCaseRoute, true);
                },
                previewTransitionalPageId: 3118
            },
            {
                app: 4001,
                canPreviewDownloads: this.accountContext.hasPermission('UM.AuthCorrespondenceLetter.Download.Preview'),
                showSummaryLink: this.accountContext.hasRouteAccess('um.auth.summary'),
                openSummary: (id: string, number: string) => {
                    const c = { id, number, member: { id: this.memberId } } as UM.Auth;
                    this.openAuthSummary(c, this.hasAuthRoute, true);
                },
                previewTransitionalPageId: 4054
            },
            {
                app: 6001,
                canPreviewDownloads: true,// this.accountContext.hasPermission('AG.CaseCorrespondenceLetter.Download.Preview'),
                showSummaryLink: this.accountContext.hasRouteAccess('6004'),//'ag.case.summary'
                transitionalSummaryId: 6004,
                previewTransitionalPageId: 6054
            },
            {
                app: 14001,
                canPreviewDownloads: true,// this.accountContext.hasPermission('PM.CandidateCorrespondenceLetter.Download.Preview'),
                showSummaryLink: this.accountContext.hasRouteAccess('14007'),//'pm.candidate.summary'
                transitionalSummaryId: 14007,
                previewTransitionalPageId: 14068
            },
            {
                app: 14003,
                canPreviewDownloads: true,// this.accountContext.hasPermission('PM.MemberCorrespondenceLetter.Download.Preview'),
                showSummaryLink: false,
                previewTransitionalPageId: 14071
            }
        ]
    }

    protected openLetterHistory(letterId: string): void {
        const w: Window | null = open('', 'Popup_CorrespondenceHistory', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        this.transitional.openTransitional(w, '613', [letterId]);
    }

    protected canPreviewDownloads(id: number): boolean {
        const f = this.showLinks.find(f => f.app == id);
        if(!f) return false;
        return f.canPreviewDownloads;
    }
    protected showSummaryLink(id: number): boolean {
        const f = this.showLinks.find(f => f.app == id);
        if(!f) return false;
        return f.showSummaryLink;
    }
    protected getTransitionalSummaryId(id: number): number {
        const f = this.showLinks.find(f => f.app == id);
        if(!f || !f.transitionalSummaryId) return 0;
        return f.transitionalSummaryId;
    }
    protected openSummary(target: Core.CorrespondenceLetterTargetModel) {
        if(!target.type?.id || !target.id || !target.description) return;
        const targetTypeId = this.formatProvider.parseNumber(target.type?.id);
        const f = this.showLinks.find(f => f.app == targetTypeId);
        if(!f) return;
        f.openSummary!(target.id, target.description);
    }
    private getPreviewTransitionalPageId(id: string): string {
        const targetTypeId = this.formatProvider.parseNumber(id);
        const f = this.showLinks.find(f => f.app == targetTypeId);
        if(!f) return "";
        return f.previewTransitionalPageId.toString();
    }
    protected openLetterPreview(letter: Core.CorrespondenceLetter) {
        if(!letter.attachedTo?.type?.id) { return; }
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds: string[] = [letter.id!, '']; //transitional query string in correspondence config is expecting two parameters
        this.transitional.openTransitional(w, this.getPreviewTransitionalPageId(letter.attachedTo?.type?.id), transitionalIds);
    }

    protected openLetterCopyPreview(copy: Core.CorrespondenceLetterCopy, letter: Core.CorrespondenceLetter) {
        if(!letter?.attachedTo?.type?.id) { return; }
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds: string[] = [letter.id!, copy.id!];
        this.transitional.openTransitional(w, this.getPreviewTransitionalPageId(letter.attachedTo?.type?.id), transitionalIds);
    }

    protected applyTableDisplay(letters: Core.CorrespondenceLetter[]): ReadonlyArray<CD.MemberRollupCorrespondenceLetter> {
        return letters.map((letter) => {
            return {
                ...letter,
                $essTableDisplay: this.i18n.tr('ui:labels.for', { what: letter.template?.description, item: letter.description })
            } as CD.MemberRollupCorrespondenceLetter;
        }) as ReadonlyArray<CD.MemberRollupCorrespondenceLetter>;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadMemberLetters(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        const includes: string[] = [
            'template',
            'template.version',
            'template.version.language',
            'attachedTo',
            'attachedTo.type',
            'status',
            'copies',
            'copies.deliveryMethod',
            'copies.delivered.recipient',
            'copies.status',
            'copies.coverSheet.templateVersion.template',
            'metadata.modified.byUser'
        ];

        const fields: string[] = [
            'attachedTo.description',
            'attachedTo.type.code',
            'copies.coverSheet.templateVersion.template.description',
            'copies.delivered.recipient.description',
            'copies.deliveryMethod.description',
            'copies.status.description',
            'description',
            'letter.description',
            'letter.template?.description',
            'metadata.modified.byUser.description',
            'metadata.modified.timestamp',
            'status.description',
            'template.description',
            'template.version.language.description',
        ];

        try {
            this.state.beginLoad();

            //let response = await this.memberService.loadRollupLetters(this.memberId, options, includes) would serverside the filter
            //this is a TODO for the member-service
            const response = await this.service.loadLettersByMember(this.memberId, undefined, options, includes, fields);

            this.memberLetters = this.applyTableDisplay(response.results);
            this.memberLettersClone = deepClone(this.memberLetters);
            this.nestedLocalSorting = {};
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
